import React from 'react'
import { Camera, ImagePlus, RefreshCw, Info, CheckCircle, AlertTriangle, Circle, Eye, List, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import { RoomSection } from '@/types'
import { SectionStatus } from '@/utils/sectionStatus'

export interface ValidationCounts {
  match: number
  mismatch: number
  missing: number
  unexpected: number
}

interface MobileSectionCardProps {
  section: RoomSection
  status: SectionStatus
  imageCount: number
  detectionCount: number
  isActive: boolean
  isSessionCompleted: boolean
  uploadedImageUrl?: string | null
  validationCounts?: ValidationCounts | null
  onTakePhoto: () => void
  onChooseGallery: () => void
  onViewDetails: () => void
  onReupload?: () => void
  onViewDetections?: () => void
  onViewValidation?: () => void
}

// Validation count colors matching the legend
const VALIDATION_COLORS = {
  match: 'text-emerald-600',
  mismatch: 'text-amber-500',
  missing: 'text-red-400',
  unexpected: 'text-blue-400',
}

const STATUS_CONFIG: Record<SectionStatus, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
  empty: {
    color: 'text-red-600',
    bgColor: 'bg-red-50 border-red-200',
    icon: <Circle className="h-4 w-4" />,
    label: 'No images',
  },
  mismatch: {
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 border-amber-200',
    icon: <AlertTriangle className="h-4 w-4" />,
    label: 'Needs attention',
  },
  complete: {
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 border-emerald-200',
    icon: <CheckCircle className="h-4 w-4" />,
    label: 'Complete',
  },
}

const MobileSectionCard: React.FC<MobileSectionCardProps> = ({
  section,
  status,
  imageCount,
  detectionCount,
  isActive,
  isSessionCompleted,
  uploadedImageUrl,
  validationCounts,
  onTakePhoto,
  onChooseGallery,
  onViewDetails,
  onReupload,
  onViewDetections,
  onViewValidation,
}) => {
  const config = STATUS_CONFIG[status]
  const hasImage = imageCount > 0
  const expectedItemsCount = section.expected_items?.reduce((sum, item) => sum + item.expected_quantity, 0) || 0

  // Determine what to display:
  // - If we have an uploaded image URL, show it
  // - If there's an image but URL not loaded yet, show loading state (not section preview)
  // - If no image, show section preview or "No preview"
  const isImageLoading = hasImage && !uploadedImageUrl
  const displayImageUrl = uploadedImageUrl || (!hasImage ? section.image_path : null)

  return (
    <div
      className={cn(
        'flex flex-col bg-white rounded-2xl border-2 shadow-sm overflow-hidden transition-all h-full',
        'w-full',
        isActive ? 'border-primary-400 shadow-lg' : 'border-gray-200',
        config.bgColor
      )}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b bg-white/80">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-lg truncate flex-1">
            {section.name}
          </h3>
          <button
            onClick={onViewDetails}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-lg"
            aria-label="View section details"
          >
            <Info className="h-5 w-5" />
          </button>
        </div>

        {/* Status Badge */}
        <div className={cn('flex items-center gap-1.5 mt-1', config.color)}>
          {config.icon}
          <span className="text-sm font-medium">{config.label}</span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3 flex-1 min-h-0 flex flex-col">
        {/* Image - tappable to view detections if uploaded */}
        {isImageLoading ? (
          // Loading state while uploaded image URL is being fetched
          <div className="w-full flex-1 min-h-0 rounded-lg bg-gray-100 mb-3 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : displayImageUrl ? (
          <button
            onClick={hasImage && onViewDetections ? onViewDetections : undefined}
            disabled={!hasImage || !onViewDetections}
            className={cn(
              'w-full flex-1 min-h-0 rounded-lg overflow-hidden bg-gray-100 mb-3 relative',
              hasImage && onViewDetections && 'active:opacity-90 cursor-pointer'
            )}
          >
            <img
              src={displayImageUrl}
              alt={section.name}
              className="w-full h-full object-cover"
            />
            {/* Tap indicator overlay for uploaded images */}
            {hasImage && onViewDetections && (
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  Tap to view
                </div>
              </div>
            )}
          </button>
        ) : (
          <div className="w-full flex-1 min-h-0 rounded-lg bg-gray-100 mb-3 flex items-center justify-center">
            <span className="text-gray-400 text-sm">No preview</span>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 flex-shrink-0">
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <div className="text-xl font-bold text-gray-800">{expectedItemsCount}</div>
            <div className="text-xs text-gray-500">Expected items</div>
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            {validationCounts ? (
              <>
                <div className="flex items-center justify-center gap-0.5 text-base font-bold">
                  <span className={VALIDATION_COLORS.match}>{validationCounts.match}</span>
                  <span className="text-gray-400">/</span>
                  <span className={VALIDATION_COLORS.mismatch}>{validationCounts.mismatch}</span>
                  <span className="text-gray-400">/</span>
                  <span className={VALIDATION_COLORS.missing}>{validationCounts.missing}</span>
                  <span className="text-gray-400">/</span>
                  <span className={VALIDATION_COLORS.unexpected}>{validationCounts.unexpected}</span>
                </div>
                <div className="text-xs text-gray-500">Validation</div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-gray-800">{detectionCount}</div>
                <div className="text-xs text-gray-500">Detected</div>
              </>
            )}
          </div>
        </div>

        {/* Action buttons when there are images */}
        {hasImage && (
          <div className="grid grid-cols-2 gap-2 mt-3 flex-shrink-0">
            {onViewDetections && (
              <button
                onClick={onViewDetections}
                className={cn(
                  'flex items-center justify-center gap-1.5 py-2 rounded-lg',
                  'bg-primary-100 text-primary-700 font-medium text-sm',
                  'active:bg-primary-200 transition-colors'
                )}
              >
                <Eye className="h-4 w-4" />
                Detections
              </button>
            )}
            {onViewValidation && (
              <button
                onClick={onViewValidation}
                className={cn(
                  'flex items-center justify-center gap-1.5 py-2 rounded-lg',
                  'bg-gray-100 text-gray-700 font-medium text-sm',
                  'active:bg-gray-200 transition-colors'
                )}
              >
                <List className="h-4 w-4" />
                Validation
              </button>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      {!isSessionCompleted && (
        <div className="px-4 pb-3 space-y-2 flex-shrink-0">
          {!hasImage ? (
            // Upload buttons for empty section
            <>
              <button
                onClick={onTakePhoto}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl',
                  'bg-primary-600 text-white font-medium',
                  'active:bg-primary-700 transition-colors'
                )}
              >
                <Camera className="h-5 w-5" />
                Take Photo
              </button>
              <button
                onClick={onChooseGallery}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl',
                  'bg-white border-2 border-gray-300 text-gray-700 font-medium',
                  'active:bg-gray-50 transition-colors'
                )}
              >
                <ImagePlus className="h-5 w-5" />
                Choose from Gallery
              </button>
            </>
          ) : (
            // Reupload button for sections with images
            <button
              onClick={onReupload || onChooseGallery}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl',
                'bg-white border-2 border-gray-300 text-gray-700 font-medium',
                'active:bg-gray-50 transition-colors'
              )}
            >
              <RefreshCw className="h-5 w-5" />
              Reupload Image
            </button>
          )}
        </div>
      )}

      {/* Completed Session Notice */}
      {isSessionCompleted && (
        <div className="px-4 pb-4">
          <div className="text-center text-sm text-gray-500 py-2">
            Session completed
          </div>
        </div>
      )}
    </div>
  )
}

export default MobileSectionCard
